import { css } from "@emotion/css";
import { useEffect, useCallback, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../../shared/components/navbar/navbar";
import Footer from "../../shared/components/footer/footer";
import {
  useCreateOrderSession,
  useGetOrderSession,
  useBookSeats,
  useSetUserInfo,
  useProcessPayment,
  useClaimGuestSession,
} from "../../features/checkout/hooks/checkout-api.hooks";
import CheckoutSeats from "../../features/checkout/components/checkout-seats/checkout-seats";
import { Stepper } from "../../shared/ui/stepper";
import CartSummary, {
  type CartSeat,
} from "../../features/checkout/components/cart-summary/cart-summary";
import { toast } from "react-toastify";
import {
  CheckoutUserInfoForm,
  type UserInfoFormData,
} from "../../features/checkout/components/checkout-user-info-form";
import { PaymentCheckout } from "../../features/checkout/components/payment-checkout";
// Removed event coupons modal and schedule detail hook (now using a simple input modal)
import { useApplyCoupon } from "../../features/profile/hooks/profile-api.hooks";
import {
  getIssuerIdFromCardNumber,
  getPaymentMethodIdFromCardNumber,
  generateCardToken,
} from "../../features/checkout/utils/payment.utils";
import { SessionTimer } from "../../features/checkout/components/session-timer";
import { PaymentSuccess } from "../../features/checkout/components/payment-success/index";
import { useLoading } from "../../features/loading";
import { useAuth } from "../../features/auth/hooks/use-auth";

const pageWrapper = css({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "var(--primary-color)",
});

const content = css({
  flex: 1,
  maxWidth: "1440px",
  width: "100%",
  margin: "0 auto",
  padding: "var(--spacing-xl) var(--spacing-md)",
  paddingBottom: "calc(var(--spacing-xl) + 80px)",
  "@media (max-width: 768px)": {
    padding: "var(--spacing-md) var(--spacing-sm)",
    paddingBottom: "calc(var(--spacing-md) + 80px)",
  },
});

const gridLayout = css({
  display: "grid",
  gridTemplateColumns: "2fr 1fr",
  gap: "var(--spacing-2xl)",
  alignItems: "start",
  "@media (max-width: 920px)": {
    gridTemplateColumns: "1fr",
    gap: "var(--spacing-xl)",
  },
});

const mainContent = css({
  backgroundColor: "var(--primary-color)",
  borderRadius: "var(--spacing-md)",
  padding: "var(--spacing-xl)",
  boxShadow: "var(--shadow-300)",
  border: "1px solid rgba(var(--pcrgba-200), 0.5)",
  minHeight: "400px",
  "@media (max-width: 768px)": {
    padding: "var(--spacing-lg)",
  },
});

const placeholder = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "400px",
  color: "var(--pc-600)",
  textAlign: "center",
  gap: "var(--spacing-md)",
  "& h2": {
    fontSize: "var(--fs-500)",
    color: "var(--pc-700)",
    margin: 0,
  },
  "& p": {
    fontSize: "var(--fs-400)",
    color: "var(--pc-600)",
    margin: 0,
  },
});

const navButton = css({
  padding: "var(--spacing-sm) var(--spacing-xl)",
  borderRadius: "var(--spacing-sm)",
  fontWeight: 600,
  fontSize: "var(--fs-400)",
  cursor: "pointer",
  transition: "all 0.2s ease",
  border: "none",
  minWidth: "120px",
  backgroundColor: "var(--pc-600)",
  color: "var(--primary-color)",
});

const bottomNav = css({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: "var(--primary-color)",
  borderTop: "1px solid rgba(var(--pcrgba-200), 0.5)",
  boxShadow: "0 -4px 12px rgba(var(--pcrgba-500), 0.1)",
  zIndex: 100,
});

const sessionTimerWrapper = css({
  position: "fixed",
  left: 0,
  width: "100%",
  zIndex: 99,
});

const bottomNavContent = css({
  maxWidth: "1440px",
  margin: "0 auto",
  padding: "var(--spacing-md) var(--spacing-lg)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  "@media (max-width: 768px)": {
    padding: "var(--spacing-sm) var(--spacing-md)",
  },
});

const stepperContainer = css({
  marginBottom: "var(--spacing-2xl)",
  "@media (max-width: 768px)": {
    marginBottom: "var(--spacing-xl)",
  },
});
// Steps variants (with and without the information step)
const STEPS_WITH_INFO = [
  { label: "Assentos", number: 1 },
  { label: "Informações", number: 2 },
  { label: "Pagamento", number: 3 },
  { label: "Concluído", number: 4 },
];

const STEPS_WITHOUT_INFO = [
  { label: "Assentos", number: 1 },
  { label: "Pagamento", number: 2 },
  { label: "Concluído", number: 3 },
];

// Coupons UI styles
const couponContainer = css({
  marginTop: "var(--spacing-xl)",
  paddingTop: "var(--spacing-lg)",
  borderTop: "1px solid rgba(var(--pcrgba-200), 0.5)",
  display: "grid",
  gap: "var(--spacing-md)",
});

const couponRow = css({
  display: "flex",
  gap: "var(--spacing-sm)",
  alignItems: "center",
  flexWrap: "wrap",
});

const couponInputStyle = css({
  flex: "1 1 240px",
  minWidth: "200px",
  border: "1px solid rgba(var(--pcrgba-200), 0.8)",
  borderRadius: "8px",
  padding: "4px 10px",
  fontSize: "var(--fs-400)",
  height: "28px",
  minHeight: "28px",
  maxHeight: "28px",
  lineHeight: "20px",
  boxSizing: "border-box",
  textTransform: "uppercase",
  display: "inline-block",
  flexGrow: 0,
  flexShrink: 0,
});

const couponPill = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "6px 10px",
  borderRadius: "999px",
  backgroundColor: "rgba(var(--pcrgba-200), 0.2)",
  color: "var(--pc-700)",
  fontSize: "var(--fs-300)",
  width: "fit-content",
});

export function Checkout() {
  const navigate = useNavigate();
  const { step: routeStep } = useParams<{ step: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const scheduleId = searchParams.get("scheduleId") || "";
  const sessionId = searchParams.get("sessionId") || "";
  const orderUuid = searchParams.get("orderUuid") || "";

  const { isAuthenticated, user } = useAuth();
  const createSessionMutation = useCreateOrderSession();
  // Use session data to avoid rebooking already-reserved seats
  const { data: orderSession, refetch: refetchOrderSession } =
    useGetOrderSession(sessionId, !!sessionId);
  const bookSeatsMutation = useBookSeats();
  const setUserInfoMutation = useSetUserInfo();
  const processPaymentMutation = useProcessPayment();
  const claimSessionMutation = useClaimGuestSession();
  const { setLoading, isLoading } = useLoading();

  // Local state for summary selections
  const [selectedSeats, setSelectedSeats] = useState<CartSeat[]>([]);
  const [userInfoData, setUserInfoData] = useState<UserInfoFormData | null>(
    null
  );
  const [paymentValid, setPaymentValid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<
    "credit" | "debit" | "pix" | "balance" | null
  >(null);
  const [paymentData, setPaymentData] = useState<any | null>(null);
  const [showCouponsModal, setShowCouponsModal] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [couponInput, setCouponInput] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const [navbarElement, setNavbarElement] = useState<HTMLElement | null>(null);

  // Callback ref to capture navbar element
  const navbarRef = useCallback((node: HTMLElement | null) => {
    if (node) {
      setNavbarElement(node);
    }
  }, []);

  // Detect navbar height dynamically
  useEffect(() => {
    if (!navbarElement) return;

    const updateNavbarHeight = () => {
      const height = navbarElement.getBoundingClientRect().height;
      setNavbarHeight(height);
    };

    // Update on mount and window resize
    updateNavbarHeight();
    window.addEventListener("resize", updateNavbarHeight);

    return () => {
      window.removeEventListener("resize", updateNavbarHeight);
    };
  }, [navbarElement]);

  // Track all loading states for global loading screen
  useEffect(() => {
    const isAnyLoading =
      createSessionMutation.isPending ||
      bookSeatsMutation.isPending ||
      setUserInfoMutation.isPending ||
      processPaymentMutation.isPending;

    setLoading(isAnyLoading);
  }, [
    createSessionMutation.isPending,
    bookSeatsMutation.isPending,
    setUserInfoMutation.isPending,
    processPaymentMutation.isPending,
    setLoading,
  ]);

  // Create session on mount if needed
  useEffect(() => {
    if (
      scheduleId &&
      !sessionId &&
      !createSessionMutation.isPending &&
      !createSessionMutation.isError
    ) {
      createSessionMutation.mutate(
        { scheduleId },
        {
          onSuccess: (response) => {
            setSearchParams({
              scheduleId,
              sessionId: response.session.sessionId,
            });
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, sessionId]);

  // Claim guest session if user is logged in
  useEffect(() => {
    if (isAuthenticated && sessionId && !claimSessionMutation.isPending) {
      claimSessionMutation.mutate(
        { sessionId },
        {
          onSuccess: () => {
            console.log("Guest session claimed successfully");
          },
          onError: (error: any) => {
            // Silently fail - session might already be claimed or user's own session
            console.log(
              "Session claim info:",
              error?.response?.data?.message ||
                "Session already claimed or owned by user"
            );
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, sessionId]);

  // Check if user has all required profile data
  const userHasCompleteProfile = useMemo(() => {
    if (!isAuthenticated || !user) return false;
    return !!(user.name && user.email && user.phone && user.cpf);
  }, [isAuthenticated, user]);

  // Handle allowed steps based on whether user has complete profile
  const allowedSteps = useMemo(() => {
    if (userHasCompleteProfile) {
      // Skip step 2 (informacoes) if user has complete profile
      return ["assentos", "pagamento", "concluido"] as const;
    }
    return ["assentos", "informacoes", "pagamento", "concluido"] as const;
  }, [userHasCompleteProfile]);

  // Use appropriate step labels based on whether info step is included
  const steps = useMemo(() => {
    return userHasCompleteProfile ? STEPS_WITHOUT_INFO : STEPS_WITH_INFO;
  }, [userHasCompleteProfile]);

  const currentStepIndex = useMemo(() => {
    const idx = allowedSteps.indexOf((routeStep as any) || "assentos");
    return idx >= 0 ? idx : 0;
  }, [routeStep, allowedSteps]);

  // Redirect to seats if step is invalid
  useEffect(() => {
    if (!routeStep || !allowedSteps.includes(routeStep as any)) {
      const query = new URLSearchParams();
      if (scheduleId) query.set("scheduleId", scheduleId);
      if (sessionId) query.set("sessionId", sessionId);
      navigate(`/finalizar-compra/assentos?${query.toString()}`, {
        replace: true,
      });
    }
  }, [routeStep, allowedSteps, navigate, scheduleId, sessionId]);

  // Map seats selection from CheckoutSeats to CartSummary shape
  const handleSeatsChange = useCallback((seats: any[]) => {
    const mapped: CartSeat[] = seats.map((s) => ({
      id: `${s.zone}-${s.row}-${s.column}-${s.ticketType}`,
      row: s.row,
      column: s.column,
      zone: s.zone,
      ticketType: s.ticketType,
      price: Number(s.price) || 0,
      selected: true,
      seatsPerTicket: s.seatsPerTicket ? Number(s.seatsPerTicket) : 1,
      multiGroupId: s.multiGroupId,
    }));
    setSelectedSeats(mapped);
  }, []);

  const handleContinue = useCallback(async () => {
    const currentStep = allowedSteps[currentStepIndex];

    if (currentStep === "assentos") {
      // Step 1: Seats -> book seats then go to information or payment
      if (!sessionId) {
        toast.error("Sessão inválida. Recarregue a página.", {
          position: "top-left",
        });
        return;
      }
      if (selectedSeats.length === 0) {
        toast.info("Selecione ao menos um assento para continuar.", {
          position: "top-left",
        });
        return;
      }

      // Build bookings according to ticket rules (setter semantics) with session-aware grouping
      const seatsToBook = selectedSeats;

      // Map existing session seats to their order item to preserve multi-seat grouping
      const sessionItems = orderSession?.session?.items || [];
      type SeatKey = string;
      const makeSeatKey = (z?: string, r?: string, c?: string): SeatKey =>
        `${z || ""}|${r || ""}|${c || ""}`;
      const sessionSeatMap = new Map<
        SeatKey,
        { groupId: string; ticketType: string; seatsPerTicket: number }
      >();
      sessionItems.forEach((item, index) => {
        const isMulti = (item.seats?.length || 0) > 1;
        const groupId = `session-item-${index}`;
        const seatsPerTicket = isMulti ? item.seats?.length || 1 : 1;
        item.seats?.forEach((seat) => {
          sessionSeatMap.set(makeSeatKey(seat.zone, seat.row, seat.column), {
            groupId,
            ticketType: item.ticketType,
            seatsPerTicket,
          });
        });
      });

      const grouped = new Map<
        string,
        {
          ticketType: string;
          seatsPerTicket: number;
          seats: { zone: string; row: string; column: string }[];
        }
      >();

      for (const s of seatsToBook) {
        const key = makeSeatKey(s.zone, s.row, s.column);
        const sessionInfo = sessionSeatMap.get(key);
        const seatObj = {
          zone: s.zone || "",
          row: s.row || "",
          column: s.column || "",
        };

        if (sessionInfo) {
          // Seat belongs to an existing session item; group using that item to preserve multi-seat tickets
          const g = grouped.get(sessionInfo.groupId);
          if (!g) {
            grouped.set(sessionInfo.groupId, {
              ticketType: sessionInfo.ticketType || s.ticketType || "Inteira",
              seatsPerTicket:
                sessionInfo.seatsPerTicket > 0
                  ? sessionInfo.seatsPerTicket
                  : s.seatsPerTicket || 1,
              seats: [seatObj],
            });
          } else {
            // Validate consistency
            if (
              g.ticketType !==
              (sessionInfo.ticketType || s.ticketType || "Inteira")
            ) {
              toast.error(
                "Assentos inválidos para este tipo de ingresso. Revise sua seleção.",
                { position: "top-left" }
              );
              return;
            }
            g.seats.push(seatObj);
          }
          continue;
        }

        // New seat (not yet in session). Use UI grouping (multiGroupId) or single-seat fallback
        const seatsPerTicket =
          s.seatsPerTicket && s.seatsPerTicket > 0 ? s.seatsPerTicket : 1;
        if (seatsPerTicket > 1 && s.multiGroupId) {
          const mgid = `ui-multi-${s.multiGroupId}`;
          const g = grouped.get(mgid);
          if (!g) {
            grouped.set(mgid, {
              ticketType: s.ticketType || "Inteira",
              seatsPerTicket,
              seats: [seatObj],
            });
          } else {
            if (
              g.ticketType !== (s.ticketType || "Inteira") ||
              g.seatsPerTicket !== seatsPerTicket
            ) {
              toast.error(
                "Assentos inválidos para este tipo de ingresso. Revise sua seleção.",
                { position: "top-left" }
              );
              return;
            }
            g.seats.push(seatObj);
          }
        } else {
          // Single-seat booking
          const singleKey = `single-${s.ticketType || "Inteira"}-${key}`;
          grouped.set(singleKey, {
            ticketType: s.ticketType || "Inteira",
            seatsPerTicket: 1,
            seats: [seatObj],
          });
        }
      }

      // Validate multi-seat completeness for all groups with seatsPerTicket > 1
      for (const g of grouped.values()) {
        if (g.seatsPerTicket > 1 && g.seats.length !== g.seatsPerTicket) {
          toast.error(
            `Este ingresso exige ${g.seatsPerTicket} assentos. Você selecionou ${g.seats.length}.`,
            { position: "top-left" }
          );
          return;
        }
      }

      const bookings = Array.from(grouped.values()).map((g) => ({
        ticketType: g.ticketType,
        seats: g.seats,
      }));

      try {
        await bookSeatsMutation.mutateAsync({ sessionId, bookings });
        toast.success("Assentos reservados com sucesso!", {
          position: "top-left",
        });

        // If user has complete profile, skip to payment and auto-set user info
        if (userHasCompleteProfile && user) {
          try {
            await setUserInfoMutation.mutateAsync({
              sessionId,
              userInfo: {
                name: user.name!,
                email: user.email,
                phone: user.phone!,
                document: user.cpf!,
              },
            });
          } catch (err: any) {
            const msg =
              err?.response?.data?.message ||
              "Não foi possível salvar suas informações.";
            toast.error(msg, { position: "top-left" });
            return;
          }
        }

        const query = new URLSearchParams();
        if (scheduleId) query.set("scheduleId", scheduleId);
        if (sessionId) query.set("sessionId", sessionId);

        // Go to payment if profile is complete, otherwise go to informacoes
        const nextStep = userHasCompleteProfile ? "pagamento" : "informacoes";
        navigate(`/finalizar-compra/${nextStep}?${query.toString()}`);
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          "Não foi possível reservar os assentos.";
        toast.error(msg, { position: "top-left" });
      }
      return;
    }

    if (currentStep === "informacoes") {
      // Step 2: Information -> set user info then go to payment
      if (!sessionId) {
        toast.error("Sessão inválida. Recarregue a página.", {
          position: "top-left",
        });
        return;
      }
      if (!userInfoData || !userInfoData.isValid) {
        toast.info("Preencha suas informações corretamente para continuar.", {
          position: "top-left",
        });
        return;
      }
      try {
        await setUserInfoMutation.mutateAsync({
          sessionId,
          userInfo: {
            name: userInfoData.name,
            email: userInfoData.email,
            phone: userInfoData.phone,
            document: userInfoData.document,
          },
        });
        toast.success("Informações salvas com sucesso!", {
          position: "top-left",
        });
        const query = new URLSearchParams();
        if (scheduleId) query.set("scheduleId", scheduleId);
        if (sessionId) query.set("sessionId", sessionId);
        navigate(`/finalizar-compra/pagamento?${query.toString()}`);
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          "Não foi possível salvar suas informações.";
        toast.error(msg, { position: "top-left" });
      }
      return;
    }

    if (currentStep === "pagamento") {
      // Step 3: Payment -> process payment then go to concluido
      if (!sessionId) {
        toast.error("Sessão inválida. Recarregue a página.", {
          position: "top-left",
        });
        return;
      }
      if (!paymentMethod) {
        toast.info("Selecione um método de pagamento.", {
          position: "top-left",
        });
        return;
      }
      // Only validate payment data for credit and debit cards
      if (
        (paymentMethod === "credit" || paymentMethod === "debit") &&
        !paymentValid
      ) {
        const methodLabel =
          paymentMethod === "credit" ? "cartão de crédito" : "cartão de débito";
        toast.info(`Preencha os dados do ${methodLabel}.`, {
          position: "top-left",
        });
        return;
      }

      // Map UI method/data to API request
      const req: any = {
        sessionId,
        paymentMethod: "pix", // default; will overwrite below
      };

      if (paymentMethod === "credit") {
        // Determine payment method id from component or BIN
        let methodId: string | null = paymentData?.paymentMethodId || null;
        if (!methodId && paymentData?.cardNumber) {
          methodId = await getPaymentMethodIdFromCardNumber(
            paymentData.cardNumber,
            "credit_card"
          );
        }
        req.paymentMethod = methodId || "credit_card";
        req.installments = paymentData?.installments || 1;
        console.log(paymentData);
        // Fallback: if the token wasn't generated in the form, try to generate it here
        if (!paymentData?.cardToken) {
          try {
            const token = await generateCardToken({
              cardNumber: String(paymentData?.cardNumber || "").replace(
                /\D/g,
                ""
              ),
              cardholderName: String(paymentData?.cardholderName || ""),
              cardExpirationMonth: String(paymentData?.expirationMonth || ""),
              cardExpirationYear: `20${String(
                paymentData?.expirationYear || ""
              )}`,
              securityCode: String(paymentData?.securityCode || ""),
            });

            if (token?.id) {
              req.cardToken = token.id;
            } else {
              toast.error(
                "Não foi possível gerar o token do cartão. Verifique os dados e tente novamente.",
                { position: "top-left" }
              );
              return;
            }
          } catch (e) {
            toast.error(
              "Não foi possível gerar o token do cartão. Verifique os dados e tente novamente.",
              { position: "top-left" }
            );
            return;
          }
        } else {
          req.cardToken = paymentData.cardToken;
        }
        if (paymentData?.issuerId) {
          req.issuerId = Number(paymentData.issuerId);
        } else if (paymentData?.cardNumber) {
          const issuer = await getIssuerIdFromCardNumber(
            paymentData.cardNumber,
            "credit_card"
          );
          if (issuer) req.issuerId = issuer;
        }
        if (!req.issuerId) {
          toast.error(
            "Não foi possível identificar o emissor do cartão. Verifique os dados e tente novamente.",
            { position: "top-left" }
          );
          return;
        }
      }
      if (paymentMethod === "debit") {
        let methodId: string | null = paymentData?.paymentMethodId || null;
        if (!methodId && paymentData?.cardNumber) {
          methodId = await getPaymentMethodIdFromCardNumber(
            paymentData.cardNumber,
            "debit_card"
          );
        }
        req.paymentMethod = methodId || "debit_card";
      }
      if (paymentMethod === "pix") {
        req.paymentMethod = "pix";
      }
      if (paymentMethod === "balance") {
        req.paymentMethod = "balance";
      }

      try {
        const paymentResp = await processPaymentMutation.mutateAsync(req);
        toast.success("Pagamento processado com sucesso!", {
          position: "top-left",
        });
        const query = new URLSearchParams();
        if (paymentResp?.orderId) query.set("orderUuid", paymentResp.orderId);
        navigate(`/finalizar-compra/concluido?${query.toString()}`);
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Não foi possível processar o pagamento.";
        toast.error(msg, { position: "top-left" });
      }
      return;
    }
  }, [
    currentStepIndex,
    sessionId,
    selectedSeats,
    bookSeatsMutation,
    navigate,
    scheduleId,
    userInfoData,
    setUserInfoMutation,
    userHasCompleteProfile,
    user,
    orderSession,
  ]);

  const isCreatingSession = createSessionMutation.isPending;
  const sessionCreationFailed = createSessionMutation.isError;

  // schedule detail not required for coupon input flow

  const applyCouponMutation = useApplyCoupon();

  const handleManualApplyCoupon = async () => {
    if (!sessionId) return;
    const code = couponInput.trim().toUpperCase();
    if (!code || isApplyingCoupon) return;
    try {
      setIsApplyingCoupon(true);
      await applyCouponMutation.mutateAsync({
        sessionId,
        couponCode: code,
      });
      setAppliedCoupon(code);
      toast.success("Cupom aplicado!", { position: "top-left" });
      await refetchOrderSession();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || "Cupom inválido ou não aplicável.";
      toast.error(msg, { position: "top-left" });
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  // Don't render anything while loading
  if (isLoading || isCreatingSession) {
    return null;
  }

  if (!routeStep) return null;

  // Show error if session creation failed (only after loading is complete)
  if (sessionCreationFailed && !sessionId) {
    return (
      <>
        <Navbar ref={navbarRef} />
        <div className={pageWrapper}>
          <div className={content}>
            <div className={mainContent}>
              <div className={placeholder}>
                <h2>Erro ao criar sessão</h2>
                <p>Não foi possível iniciar o checkout. Tente novamente.</p>
                <button
                  className={navButton}
                  onClick={() => navigate(-1)}
                  style={{ marginTop: "var(--spacing-lg)" }}
                >
                  Voltar
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar ref={navbarRef} />
      {/* Session timer below navbar */}
      {sessionId && currentStepIndex < steps.length - 1 && (
        <SessionTimer
          className={sessionTimerWrapper}
          style={{ top: `${navbarHeight}px` }}
          sessionId={sessionId}
          onExpired={() => {
            toast.error("Sua sessão expirou. Redirecionando...", {
              position: "top-left",
            });
            navigate("/");
          }}
        />
      )}
      <div className={pageWrapper}>
        <div className={content}>
          <div className={stepperContainer}>
            <Stepper steps={steps} currentStep={currentStepIndex} />
          </div>
          <div className={gridLayout}>
            <div className={mainContent}>
              {allowedSteps[currentStepIndex] === "assentos" && (
                <CheckoutSeats
                  sessionId={sessionId}
                  onSeatsChange={handleSeatsChange}
                />
              )}
              {allowedSteps[currentStepIndex] === "informacoes" && (
                <CheckoutUserInfoForm
                  existingUserInfo={orderSession?.session?.userInfo}
                  onDataChange={(data) => setUserInfoData(data)}
                />
              )}
              {allowedSteps[currentStepIndex] === "pagamento" && (
                <PaymentCheckout
                  totalValue={Number(orderSession?.session?.totalAmount || 0)}
                  document={orderSession?.session?.userInfo?.document}
                  onValidationChange={(valid) => setPaymentValid(valid)}
                  onPaymentDataChange={(data, method) => {
                    setPaymentData(data);
                    setPaymentMethod(method);
                  }}
                />
              )}
              {allowedSteps[currentStepIndex] === "pagamento" && (
                <div className={couponContainer}>
                  <div className={couponRow}>
                    <button
                      className={navButton}
                      onClick={() => setShowCouponsModal(true)}
                    >
                      Adicionar Cupom
                    </button>
                  </div>
                  {appliedCoupon && (
                    <div className={couponPill}>
                      Cupom aplicado: <strong>{appliedCoupon}</strong>
                    </div>
                  )}
                </div>
              )}
              {allowedSteps[currentStepIndex] === "concluido" && (
                <PaymentSuccess orderId={orderUuid} />
              )}
            </div>
            {allowedSteps[currentStepIndex] === "concluido" ? (
              <CartSummary orderId={orderUuid} />
            ) : (
              <CartSummary sessionId={sessionId} />
            )}
          </div>
        </div>
      </div>
      {currentStepIndex < steps.length - 1 && (
        <div className={bottomNav}>
          <div className={bottomNavContent}>
            <button
              className={navButton}
              onClick={() => {
                if (currentStepIndex === 0) {
                  navigate(-1);
                  return;
                }
                const prevStep = allowedSteps[currentStepIndex - 1];
                const query = new URLSearchParams();
                if (scheduleId) query.set("scheduleId", scheduleId);
                if (sessionId) query.set("sessionId", sessionId);
                navigate(`/finalizar-compra/${prevStep}?${query.toString()}`);
              }}
            >
              Voltar
            </button>
            <button
              className={navButton}
              onClick={handleContinue}
              disabled={
                (allowedSteps[currentStepIndex] === "assentos" &&
                  bookSeatsMutation.isPending) ||
                (allowedSteps[currentStepIndex] === "informacoes" &&
                  setUserInfoMutation.isPending) ||
                (allowedSteps[currentStepIndex] === "pagamento" &&
                  processPaymentMutation.isPending) ||
                false
              }
            >
              {allowedSteps[currentStepIndex] === "assentos" &&
                (bookSeatsMutation.isPending ? "Reservando..." : "Continuar")}
              {allowedSteps[currentStepIndex] === "informacoes" &&
                (setUserInfoMutation.isPending
                  ? "Salvando..."
                  : "Ir para pagamento")}
              {allowedSteps[currentStepIndex] === "pagamento" &&
                (processPaymentMutation.isPending
                  ? "Processando..."
                  : "Finalizar compra")}
            </button>
          </div>
        </div>
      )}
      {/* Modal customizado para adicionar cupom manualmente */}
      {showCouponsModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.4)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "var(--primary-color)",
              borderRadius: 12,
              padding: 32,
              minWidth: 320,
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
              gap: 16,
              alignItems: "stretch",
            }}
          >
            <h3 style={{ margin: 0, fontSize: "1.2rem" }}>Adicionar Cupom</h3>
            <input
              className={couponInputStyle}
              placeholder="Digite o código do cupom"
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              type="text"
              style={{ marginBottom: 8, height: "28px", maxHeight: "28px" }}
              autoFocus
            />
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <button
                className={navButton}
                onClick={() => setShowCouponsModal(false)}
                type="button"
              >
                Cancelar
              </button>
              <button
                className={navButton}
                onClick={async () => {
                  await handleManualApplyCoupon();
                  if (!isApplyingCoupon && !applyCouponMutation.isPending) {
                    setShowCouponsModal(false);
                  }
                }}
                disabled={
                  !couponInput.trim() ||
                  applyCouponMutation.isPending ||
                  isApplyingCoupon
                }
                type="button"
              >
                {applyCouponMutation.isPending || isApplyingCoupon
                  ? "Aplicando…"
                  : "Aplicar"}
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
}

export default Checkout;
